import { useState, useEffect, useRef } from "react";
import { useStateValue } from "../../context/StateProvider";
import { Link, useNavigate } from "react-router-dom";
import { MoreVertical, Pencil, Eye, Trash2, CheckCircle2 } from "lucide-react";
import userlogo from "../../images/user.png";
import { formatCurrency, formatDate, formatNumberShort } from "../../utils/helper";

// Color theme for each deal stage so the status pill is scannable at a glance.
const STAGE_COLORS = {
  Prospect: "bg-slate-100 text-slate-700",
  "On Hold": "bg-slate-100 text-slate-500",
  CMAs: "bg-blue-50 text-blue-700",
  "Listing Appointment": "bg-blue-50 text-blue-700",
  "Pre-Market": "bg-indigo-50 text-indigo-700",
  "Coming Soon": "bg-indigo-50 text-indigo-700",
  "Under Contract": "bg-amber-50 text-amber-700",
  Pending: "bg-orange-50 text-orange-700",
  Active: "bg-violet-50 text-violet-700",
  Closed: "bg-emerald-50 text-emerald-700",
  Cancelled: "bg-rose-50 text-rose-700",
  Expired: "bg-rose-50 text-rose-700",
};

const DealCard = ({
  data,
  onEdit,
  onDelete,
  onAprove,
  isShowApprove = false,
  isShowEdit = false,
  viewdetails = true,
  isShowDelete = false,
  highlight,
  selectedstat,
  isStatCard = false,
  statUserId,
  role,
  onBgColor = false,
}) => {
  const [{ users, user }, dispatch] = useStateValue();
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (!viewdetails) return;
    navigate(`/dashboard/deals-detail/${data.createdat}`);
  };

  const stopAndRun = (fn) => (e) => {
    e.stopPropagation();
    fn?.();
  };

  const getUserImage = (users, createdBy, defaultImage) => {
    const user = users.find((u) => u.id === createdBy);
    console.log("find", user);

    // Agar user mila aur uski profile picture available hai
    if (user && user.profilepicture) {
      return user.profilepicture;
    }

    // Otherwise default logo
    return defaultImage;
  };

  const handleComission = (data) => {
    // ✅ Case 1: Stat Card
    if (isStatCard) {
      if (role === "Admin") {
        return data.assignedteamlead === statUserId
          ? formatCurrency(data.takehomecommissionamount)
          : formatCurrency(data.agentCommissionamount);
      }

      return formatCurrency(data.agentCommissionamount);
    }

    // ✅ Case 2: Normal card (not Stat Card)
    if (user.role === "Admin" || user.role === "Supervisor" || user.role === "Accountant") {
      return formatCurrency(data.takehomecommissionamount);
    }

    // ✅ Case 3: Normal Agent
    return formatCurrency(data.agentCommissionamount);
  };

  function dealinfo(title, description, highlight) {
    return (
      <div
        className={`flex flex-col items-center ${highlight ? "bg-[#1E293B] p-2 rounded-lg" : null} `}
      >
        <span
          className={`text-xs ${highlight ? "text-gray-200" : "text-gray-500"} mb-1`}
        >
          {title}
        </span>
        <span
          className={`font-normal ${highlight ? "text-white" : "text-slate-900"} text-center`}
        >
          {description}
        </span>
      </div>
    );
  }

  const dealwinner =
    data?.splittype == "NoSplit" ||
    (data?.splittype === "Referral" && data?.agentData == "") ||
    (data?.splittype === "TeamSplit" && data?.inhousereferral == true)
      ? "TeamLead"
      : "Agent";

  const stageColor =
    STAGE_COLORS[data.dealStage] || "bg-slate-100 text-slate-700";

  const dealAgent = users.find(
    (x) =>
      (dealwinner === "TeamLead" && x.id === data.assignedteamlead) ||
      (dealwinner === "Agent" && x.id === data.agentData),
  );

  const canApprove =
    (user.role === "Admin" &&
      data.assignedteamlead === user.id &&
      data.status === "Pending" &&
      isShowApprove) ||
    (user.role === "Supervisor" &&
      user.access?.approveDeal &&
      data.status === "Pending" &&
      isShowApprove);

  const canEdit = viewdetails && isShowEdit;
  const canDelete =
    (user.role === "Admin" && isShowDelete) ||
    (user.role === "Supervisor" && user.access?.deleteDeal && isShowDelete);

  return (
    <CompactDealRow
      data={data}
      stageColor={stageColor}
      dealAgent={dealAgent}
      commissionLabel={
        user.role === "Admin" ||
        user.role === "Supervisor" ||
        user.role === "Accountant"
          ? "Total Comm."
          : "Commission"
      }
      commissionValue={handleComission(data)}
      onBgColor={onBgColor}
      viewdetails={viewdetails}
      handleCardClick={handleCardClick}
      stopAndRun={stopAndRun}
      onEdit={onEdit}
      onDelete={onDelete}
      onAprove={onAprove}
      canApprove={canApprove}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

function CompactDealRow({
  data,
  stageColor,
  dealAgent,
  commissionLabel,
  commissionValue,
  onBgColor,
  viewdetails,
  handleCardClick,
  stopAndRun,
  onEdit,
  onDelete,
  onAprove,
  canApprove,
  canEdit,
  canDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const photo =
    data?.propertyphoto?.length > 0
      ? data.propertyphoto[0]
      : "/assets/no-image.png";

  const clientNames = (data.customers || [])
    .map((c) => c.clientName)
    .filter(Boolean)
    .join(" & ");

  return (
    <div
      onClick={handleCardClick}
      className={`${onBgColor ? "bg-gray-50" : "bg-white"} rounded-lg border border-slate-300 shadow-sm hover:border-slate-400 hover:shadow-md transition flex items-stretch ${viewdetails ? "cursor-pointer" : ""}`}
    >
      <div className="w-28 flex-shrink-0 self-stretch bg-slate-100 rounded-l-lg overflow-hidden">
        <img
          src={photo}
          onError={(e) => {
            e.currentTarget.src = "/assets/no-image.png";
          }}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between p-3">
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg font-bold text-slate-900 leading-tight truncate">
                  {formatCurrency(data.offerPrice)}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${stageColor}`}
                >
                  {data.dealStage}
                </span>
              </div>
              <div className="text-sm text-slate-700 truncate mt-0.5">
                {data.propertyaddress}
              </div>
              {clientNames && (
                <div className="text-xs text-slate-500 truncate">
                  {clientNames}
                </div>
              )}
            </div>

            <div
              ref={menuRef}
              className="relative flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="size-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
                aria-label="Card actions"
              >
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-md shadow-lg py-1 text-sm z-30">
                  {viewdetails && (
                    <Link
                      to={`/dashboard/deals-detail/${data.createdat}`}
                      className="block px-3 py-2 hover:bg-slate-50 flex items-center gap-2 no-underline text-slate-700"
                      onClick={() => setMenuOpen(false)}
                    >
                      <Eye size={14} /> View details
                    </Link>
                  )}
                  {canApprove && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onAprove?.(data);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                    >
                      <CheckCircle2 size={14} /> Approve deal
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onEdit?.(data);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                    >
                      <Pencil size={14} /> Edit deal
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete?.(data);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center gap-2 text-red-600"
                    >
                      <Trash2 size={14} /> Delete deal
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>
              <span className="text-slate-400">{commissionLabel}: </span>
              <span className="text-slate-700 font-semibold">
                {commissionValue}
              </span>
            </span>
            {data.closingdate && (
              <span>
                <span className="text-slate-400">Close: </span>
                <span className="text-slate-700">
                  {formatDate(data.closingdate)}
                </span>
              </span>
            )}
            {data.leadsource?.abbreviation && (
              <span>
                <span className="text-slate-400">Source: </span>
                <span className="text-slate-700">
                  {data.leadsource.abbreviation}
                </span>
              </span>
            )}
          </div>

          {dealAgent && (
            <div className="flex items-center gap-1.5">
              <img
                src={dealAgent.profilepicture || userlogo}
                alt=""
                className="size-5 rounded-full object-cover"
              />
              <span className="text-xs text-slate-600">
                {dealAgent.firstname} {dealAgent.lastname}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DealCard;
